/**
 * Admin X-source-entries moderation page (Slice 2 CP15 Phase 1).
 *
 * Structural clone of `Messages.tsx` (CP14) narrowed for
 * `x_source_entries`:
 *   • read-only paginated list hitting
 *     `/v2/staging/x-source-entries/admin/queue.json`;
 *   • inline four-button review-state cell on every row (approve /
 *     hide / flag / unreview), same shape as Messages.tsx / Tts.tsx /
 *     Events.tsx;
 *   • filter row: `review_state` + `page` only — NO `author_handle`
 *     filter, NO `since` filter in first slice (deferred per audit
 *     X-6 / plan §4);
 *   • hide / flag ALWAYS prompt for a reason and CP15 REQUIRES a
 *     non-empty trimmed reason at the parser — if the operator hits
 *     Cancel or leaves the box blank, the request is not sent;
 *   • approve / unreview never prompt (and the column clears to NULL
 *     through the Worker write-through rule);
 *   • no create / edit / entity-tagging affordance — x_source_entries
 *     rows are written by the ingest pipeline; the
 *     `x_source_entry_entities` link table exists but surfacing
 *     entity chips is deferred in first slice (audit X-4);
 *   • no typed payload column (opaque JSONB today — audit X-5).
 *
 * Display deltas vs Messages.tsx:
 *   • `platform_id` replaces `source_external_id` (the CP15 UNIQUE
 *     external-platform key; displayed prominently since it's the
 *     stable operator-grep key);
 *   • `author_handle` replaces `author`;
 *   • `posted_at` replaces `occurred_at` (and is NULLABLE);
 *   • `url` is rendered as plain text (no anchor — operator routes
 *     shouldn't implicitly navigate to external URLs);
 *   • `arc_id` is rendered as the raw stringified bigint (no arc
 *     slug/title JOIN — the admin queue is JOIN-free in first slice).
 *
 * Idempotency: each click generates a fresh `crypto.randomUUID()`.
 * Same-key replay returns the prior receipt; same-key / different
 * to_state returns 409 (`x_source_entry_idempotency_conflict`), which
 * we surface inline with a "retrying with fresh key" hint.
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  RecapReviewState,
  XSourceEntriesAdminQueueItem,
  XSourceEntryReviewStateValidationError,
} from '@fishstank/contracts'
import {
  fetchXSourceEntriesAdminQueue,
  mutateXSourceEntryReviewState,
} from '../lib/worker'
import { FetchedArtifact } from '../components/FetchedArtifact'
import { Shell } from '../layouts/Shell'

// crypto.randomUUID is widely available in modern browsers + jsdom;
// kept as a module-local helper for parity with Messages.tsx / Tts.tsx /
// Events.tsx so any future test harness can swap it.
function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

const BODY_PREVIEW_MAX = 140

function previewBody(body: string): string {
  if (body.length <= BODY_PREVIEW_MAX) return body
  return `${body.slice(0, BODY_PREVIEW_MAX - 1).trimEnd()}…`
}

// The four review-state options the operator may filter by. `any` is
// sent verbatim; the Worker parser translates it to "no review_state
// predicate".
type ReviewStateFilter = RecapReviewState | 'any'

const REVIEW_STATE_FILTER_OPTIONS: ReadonlyArray<{
  value: ReviewStateFilter
  label: string
}> = [
  { value: 'any', label: 'Any' },
  { value: 'unreviewed', label: 'Unreviewed' },
  { value: 'approved', label: 'Approved' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'flagged', label: 'Flagged' },
]

// ────────────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────────────

export function XSourceEntriesPage() {
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  // Default filter is `any` (show every row) — CP15 first-slice
  // posture. See `parseXSourceEntriesAdminQueueFilter` for the
  // symmetric parser default.
  const [reviewState, setReviewState] = useState<ReviewStateFilter>('any')

  const query = useQuery({
    queryKey: [
      'x-source-entries',
      'admin',
      'queue',
      { page, reviewState },
    ],
    queryFn: () =>
      fetchXSourceEntriesAdminQueue({
        page,
        review_state: reviewState,
      }),
  })

  const result = query.data

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ['x-source-entries', 'admin', 'queue'],
    })

  return (
    <Shell
      provenance={
        result && (result.kind === 'ok' || result.kind === 'empty')
          ? result.artifact.provenance
          : null
      }
    >
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">X-Source</h1>
          <p className="mt-1 text-sm text-slate-400">
            X-source-entries moderation queue. Hide / flag require a
            reason; approve / unreview clear the stored reason. First
            slice shows every state by default.
          </p>
        </div>
      </header>

      <FilterRow
        reviewState={reviewState}
        onReviewStateChange={(v) => {
          setReviewState(v)
          setPage(1)
        }}
      />

      <FetchedArtifact
        result={result}
        isLoading={query.isLoading}
        testidPrefix="x-source-entries"
        labels={{
          loading: 'Loading x-source entries…',
          errorTitle: "Couldn't load x-source entries.",
          empty: 'No x-source entries on this page.',
        }}
      >
        {(artifact) => (
          <>
            <AdminXSourceTable
              items={artifact.body.items}
              onChanged={invalidate}
            />
            <Pagination
              page={artifact.body.pagination.page}
              hasNext={artifact.body.pagination.has_next}
              totalReturned={artifact.body.pagination.total_returned}
              onChange={setPage}
            />
          </>
        )}
      </FetchedArtifact>
    </Shell>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Filter row — review_state only (no author, no since in first slice).
// ────────────────────────────────────────────────────────────────────────────

function FilterRow({
  reviewState,
  onReviewStateChange,
}: {
  reviewState: ReviewStateFilter
  onReviewStateChange: (v: ReviewStateFilter) => void
}) {
  return (
    <div
      data-testid="x-source-entries-filter-row"
      className="mb-4 flex flex-wrap items-end gap-2 rounded-md border border-slate-800 bg-fishstank-surface/40 p-3 text-xs"
    >
      <label className="flex flex-col gap-1">
        <span className="text-slate-400 uppercase tracking-wider text-[10px]">
          State
        </span>
        <select
          data-testid="x-source-entries-filter-review-state"
          className="rounded-sm border border-slate-700 bg-fishstank-ink px-2 py-1 text-slate-100"
          value={reviewState}
          onChange={(e) =>
            onReviewStateChange(e.target.value as ReviewStateFilter)
          }
        >
          {REVIEW_STATE_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Table
// ────────────────────────────────────────────────────────────────────────────

function AdminXSourceTable({
  items,
  onChanged,
}: {
  items: readonly XSourceEntriesAdminQueueItem[]
  onChanged: () => void
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid="x-source-entries-table"
        data-x-source-entries-count={items.length}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium w-20">ID</th>
            <th className="px-3 py-2 text-left font-medium">Body</th>
            <th className="px-3 py-2 text-left font-medium w-28">Handle</th>
            <th className="px-3 py-2 text-left font-medium w-44">Posted at</th>
            <th className="px-3 py-2 text-left font-medium w-48">Platform ID</th>
            <th className="px-3 py-2 text-left font-medium w-20">Arc</th>
            <th className="px-3 py-2 text-left font-medium w-44">State</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {items.map((row) => (
            <AdminXSourceRow
              key={row.id}
              row={row}
              onChanged={onChanged}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AdminXSourceRow({
  row,
  onChanged,
}: {
  row: XSourceEntriesAdminQueueItem
  onChanged: () => void
}) {
  return (
    <tr
      data-testid="x-source-entries-row"
      data-x-source-entry-id={row.id}
      data-review-state={row.review_state}
      className="hover:bg-slate-900/40"
    >
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {row.id}
      </td>
      <td className="px-3 py-1.5 text-slate-100">
        <span data-testid="x-source-entries-row-body">
          {row.body ? previewBody(row.body) : (
            <span className="text-slate-600">—</span>
          )}
        </span>
        {row.url && (
          <div
            data-testid="x-source-entries-row-url"
            className="mt-1 text-[10px] text-slate-500 font-mono truncate max-w-xl"
          >
            {row.url}
          </div>
        )}
        {row.hide_reason && (
          <div
            data-testid="x-source-entries-row-hide-reason"
            className="mt-1 text-[10px] uppercase tracking-wider text-slate-500"
          >
            reason:{' '}
            <span className="text-slate-400">{row.hide_reason}</span>
          </div>
        )}
      </td>
      <td
        data-testid="x-source-entries-row-author-handle"
        className="px-3 py-1.5 text-slate-300 text-xs font-mono"
      >
        {row.author_handle ?? <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {row.posted_at ?? <span className="text-slate-600">—</span>}
      </td>
      <td
        data-testid="x-source-entries-row-platform-id"
        className="px-3 py-1.5 font-mono text-[10px] text-slate-500 truncate"
      >
        {row.platform_id}
      </td>
      <td className="px-3 py-1.5 font-mono text-xs text-slate-400">
        {row.arc_id ?? <span className="text-slate-600">—</span>}
      </td>
      <td className="px-3 py-1.5">
        <XSourceReviewStateCell row={row} onChanged={onChanged} />
      </td>
    </tr>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Review-state cell
//
// Same 4-button shape as Messages.tsx / Tts.tsx / Events.tsx. Key
// deltas for CP15:
//   - hide/flag REQUIRE a non-empty reason at the parser level; the
//     UI enforces that the prompt returned a non-empty trimmed string
//     before firing the mutation. If the operator hits Cancel OR
//     supplies only whitespace, we bail without a network call and
//     surface the CP15 `x_source_entry_hide_reason_required` message
//     locally so the guardrail is visible.
//   - narrows on XSourceEntryReviewStateValidationError
//     (`x_source_entry_*` namespaced codes, incl. the two hide_reason
//     variants).
//   - testid prefix is `x-source-entries-review-state-*`.
// ────────────────────────────────────────────────────────────────────────────

const REVIEW_STATE_BUTTONS: ReadonlyArray<{
  state: RecapReviewState
  label: string
  className: string
  promptForReason: boolean
}> = [
  {
    state: 'approved',
    label: 'Approve',
    className:
      'rounded-sm border border-emerald-700 bg-emerald-950/30 text-emerald-200 hover:bg-emerald-900/40 disabled:opacity-30',
    promptForReason: false,
  },
  {
    state: 'hidden',
    label: 'Hide',
    className:
      'rounded-sm border border-slate-600 bg-slate-900 text-slate-200 hover:bg-slate-800 disabled:opacity-30',
    promptForReason: true,
  },
  {
    state: 'flagged',
    label: 'Flag',
    className:
      'rounded-sm border border-amber-700 bg-amber-950/30 text-amber-200 hover:bg-amber-900/40 disabled:opacity-30',
    promptForReason: true,
  },
  {
    state: 'unreviewed',
    label: 'Unreview',
    className:
      'rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-30',
    promptForReason: false,
  },
]

function xSourceEntryReviewValidationMessage(
  err: XSourceEntryReviewStateValidationError,
): string {
  switch (err.code) {
    case 'invalid_review_state':
      return `Server rejected to_state="${err.received}".`
    case 'x_source_entry_review_state_unchanged':
      return `Already at ${err.current}.`
    case 'x_source_entry_idempotency_conflict':
      return `Conflict: that key already flipped to ${err.prior_to_state} (you sent ${err.requested_to_state}). Retrying with a fresh key.`
    case 'invalid_uuid':
      return 'Internal error: idempotency key invalid. Refresh and try again.'
    case 'invalid_enum':
      return `Invalid value for "${err.field}".`
    case 'x_source_entry_not_found':
      return 'This x-source entry no longer exists.'
    case 'x_source_entry_hide_reason_required':
      return 'A non-empty reason is required to hide or flag.'
    case 'x_source_entry_hide_reason_too_long':
      return `Reason too long — max ${err.max} characters.`
    default:
      return 'Review-state flip failed.'
  }
}

function XSourceReviewStateCell({
  row,
  onChanged,
}: {
  row: XSourceEntriesAdminQueueItem
  onChanged: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<RecapReviewState | null>(null)

  async function flip(to: RecapReviewState, promptForReason: boolean) {
    if (row.review_state === to) return
    let reason: string | null = null
    if (promptForReason) {
      const supplied = window.prompt(
        `Reason for flipping #${row.id} to ${to} (required):`,
        '',
      )
      if (supplied === null) return // operator hit Cancel
      const trimmed = supplied.trim()
      if (trimmed.length === 0) {
        setError('A non-empty reason is required to hide or flag.')
        return
      }
      reason = trimmed
    }

    setError(null)
    setPending(to)
    try {
      const res = await mutateXSourceEntryReviewState(row.id, {
        body: {
          idempotency_key: newIdempotencyKey(),
          to_state: to,
          reason,
        },
      })
      if (res.kind === 'ok') {
        onChanged()
        return
      }
      if (res.kind === 'validation_error') {
        setError(xSourceEntryReviewValidationMessage(res.error))
        return
      }
      if (res.kind === 'csrf_error') {
        setError(`CSRF check failed: ${res.reason}`)
        return
      }
      if (res.kind === 'not_found') {
        setError('This x-source entry no longer exists.')
        return
      }
      setError(res.message)
    } finally {
      setPending(null)
    }
  }

  return (
    <div
      data-testid="x-source-entries-review-state-cell"
      data-x-source-entry-id={row.id}
      data-review-state={row.review_state}
      className="flex flex-col gap-1"
    >
      <span className="text-xs text-slate-300 uppercase">
        {row.review_state}
      </span>
      <div className="flex flex-wrap gap-1">
        {REVIEW_STATE_BUTTONS.map((b) => {
          const disabled = row.review_state === b.state || pending !== null
          return (
            <button
              key={b.state}
              type="button"
              data-testid="x-source-entries-review-state-button"
              data-x-source-entry-id={row.id}
              data-to-state={b.state}
              disabled={disabled}
              onClick={() => {
                void flip(b.state, b.promptForReason)
              }}
              className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${b.className}`}
            >
              {pending === b.state ? '…' : b.label}
            </button>
          )
        })}
      </div>
      {error && (
        <span
          data-testid="x-source-entries-review-state-error"
          className="text-[10px] text-red-300"
        >
          {error}
        </span>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Pagination
//
// Same shape as Messages.tsx. Worker returns `page`, `has_next`,
// `total_returned`. No jump-to-page in Phase 1.
// ────────────────────────────────────────────────────────────────────────────

function Pagination({
  page,
  hasNext,
  totalReturned,
  onChange,
}: {
  page: number
  hasNext: boolean
  totalReturned: number
  onChange: (page: number) => void
}) {
  const prevDisabled = page <= 1
  const nextDisabled = !hasNext
  return (
    <div
      data-testid="x-source-entries-pagination"
      data-page={page}
      data-has-next={hasNext}
      className="mt-4 flex items-center justify-between text-xs text-slate-400"
    >
      <span>
        Page {page} · {totalReturned} row{totalReturned === 1 ? '' : 's'}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="x-source-entries-pagination-prev"
          disabled={prevDisabled}
          onClick={() => onChange(page - 1)}
          className="rounded-sm border border-slate-700 px-2 py-0.5 text-slate-200 hover:bg-slate-800 disabled:opacity-30"
        >
          ← Prev
        </button>
        <button
          type="button"
          data-testid="x-source-entries-pagination-next"
          disabled={nextDisabled}
          onClick={() => onChange(page + 1)}
          className="rounded-sm border border-slate-700 px-2 py-0.5 text-slate-200 hover:bg-slate-800 disabled:opacity-30"
        >
          Next →
        </button>
      </div>
    </div>
  )
}
